import { useEffect, useRef, type RefObject } from "react";
import { sceneVisualManifest } from "@/assets/v2/manifest";
import type { V2Scene, ViewportProfile } from "../types";
import { sceneOrder, sceneVisualKey } from "../types";

function sourceFor(scene: V2Scene, profile: ViewportProfile): string {
  const asset = sceneVisualManifest[sceneVisualKey[scene]];
  return profile === "mobile" ? asset.mobile : asset.desktop;
}

/**
 * 桌面端极轻的镜头视差：整幅背景（含前景雾对比层）随指针缓慢平移，
 * 让"湖面"有可呼吸的纵深。移动/触屏与 reduced-motion 下自动停用。
 */
function useParallax(): RefObject<HTMLDivElement | null> {
  const layerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = layerRef.current;
    if (!element) return;
    if (!window.matchMedia("(min-width: 1100px) and (pointer: fine) and (hover: hover)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    element.style.transition = "transform .55s cubic-bezier(.22, 1, .36, 1)";
    let frame = 0;
    const apply = (x: number, y: number) => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const sx = Math.max(-1, Math.min(1, x)) * 1.5;
        const sy = Math.max(-1, Math.min(1, y)) * 1.05;
        element.style.transform = `translate3d(${sx}%, ${sy}%, 0)`;
      });
    };
    const onMove = (event: PointerEvent) => {
      apply(event.clientX / window.innerWidth * 2 - 1, event.clientY / window.innerHeight * 2 - 1);
    };
    const onLeave = () => apply(0, 0);
    const onBlur = () => { element.style.transform = ""; };

    window.addEventListener("pointermove", onMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", onLeave, { passive: true });
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("pointermove", onMove);
      document.documentElement.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("blur", onBlur);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return layerRef;
}

export function SceneBackground({ scene, previousScene, profile }: { scene: V2Scene; previousScene: V2Scene | null; profile: ViewportProfile }) {
  const asset = sceneVisualManifest[sceneVisualKey[scene]];
  const parallaxRef = useParallax();

  useEffect(() => {
    const nextScene = sceneOrder[sceneOrder.indexOf(scene) + 1];
    if (!nextScene) return;
    const image = new Image();
    image.decoding = "async";
    image.src = sourceFor(nextScene, profile);
  }, [profile, scene]);

  return (
    <div className="v2-background" aria-hidden="true">
      <div className="v2-bg-parallax" ref={parallaxRef}>
        {previousScene ? <img className="v2-background-image is-previous" src={sourceFor(previousScene, profile)} alt="" /> : null}
        <div className="v2-bg-current" key={`${profile}-${scene}`}>
          <img className="v2-background-image is-current" src={sourceFor(scene, profile)} alt="" fetchPriority="high" style={{ objectPosition: profile === "mobile" ? asset.mobilePosition : asset.desktopPosition }} />
        </div>
        <div className="v2-background-contrast" />
      </div>
    </div>
  );
}
